import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

interface PanelSettledContextValue {
    /** Whether the panel entrance animation has completed */
    isSettled: boolean;
    /** Mark the panel as settled (called by the panel's onAnimationComplete) */
    markSettled: () => void;
    /** Reset settled state (called when panel closes or context changes) */
    resetSettled: () => void;
    /** Returns a promise that resolves when the panel is settled */
    waitForSettled: () => Promise<void>;
}

const PanelSettledContext = createContext<PanelSettledContextValue | null>(null);

export function PanelSettledProvider({ children }: { children: ReactNode }) {
    const [isSettled, setIsSettled] = useState(false);
    const isSettledRef = useRef(false);
    const resolversRef = useRef<Array<() => void>>([]);

    const markSettled = useCallback(() => {
        isSettledRef.current = true;
        setIsSettled(true);
        // Resolve all pending waiters
        resolversRef.current.forEach(resolve => resolve());
        resolversRef.current = [];
    }, []);

    const resetSettled = useCallback(() => {
        isSettledRef.current = false;
        setIsSettled(false);
    }, []);

    const waitForSettled = useCallback((): Promise<void> => {
        // Use the ref for immediate check to avoid stale closure issues
        if (isSettledRef.current) {
            return Promise.resolve();
        }
        // Otherwise, create a promise that will resolve when markSettled is called
        return new Promise<void>(resolve => {
            resolversRef.current.push(resolve);
        });
    }, []); // No dependencies needed - we use ref

    return (
        <PanelSettledContext.Provider value={{ isSettled, markSettled, resetSettled, waitForSettled }}>
            {children}
        </PanelSettledContext.Provider>
    );
}

export function usePanelSettled() {
    const context = useContext(PanelSettledContext);
    if (!context) {
        // Provide a fallback for components rendered outside the provider
        // This ensures graceful degradation
        return {
            isSettled: true, // Assume settled if no provider
            markSettled: () => { },
            resetSettled: () => { },
            waitForSettled: () => Promise.resolve(),
        };
    }
    return context;
}
